import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Trophy, Sparkles, MessageCircle, X, Star } from "lucide-react";
import { useSocial } from "../modules/social/SocialContext";

// Confeti component simple implementation
function ConfettiPiece({ delay, duration }: { delay: number; duration: number }) {
  const colors = ['#FF6B35', '#FECC01', '#004E98', '#3A6EA5', '#00A8E8'];
  const randomColor = colors[Math.floor(Math.random() * colors.length)];
  
  return (
    <motion.div
      className="absolute w-2 h-2 rounded-full"
      style={{ backgroundColor: randomColor }}
      initial={{
        x: Math.random() * (typeof window !== 'undefined' ? window.innerWidth : 800),
        y: -10,
        rotate: 0,
        scale: Math.random() * 0.5 + 0.5,
      }}
      animate={{
        y: (typeof window !== 'undefined' ? window.innerHeight : 600) + 10,
        rotate: Math.random() * 720,
        x: Math.random() * (typeof window !== 'undefined' ? window.innerWidth : 800),
      }}
      transition={{
        duration,
        delay,
        ease: "linear",
      }}
    />
  );
}

function ConfettiExplosion() {
  const [pieces, setPieces] = useState<React.ReactNode[]>([]);
  
  useEffect(() => {
    setPieces(Array.from({ length: 50 }, (_, i) => (
      <ConfettiPiece
        key={i}
        delay={Math.random() * 2}
        duration={3 + Math.random() * 2}
      />
    )));
  }, []);

  return (
    <div className="fixed inset-0 pointer-events-none z-[1100]">
      {pieces}
    </div>
  );
}

export function BadgeCelebration() {
  const { badgeAwarded, clearBadgeAwarded } = useSocial();
  const [showConfetti, setShowConfetti] = useState(false);

  useEffect(() => {
    if (badgeAwarded) {
      setShowConfetti(true);
      const timer = setTimeout(() => setShowConfetti(false), 3000);
      return () => clearTimeout(timer);
    }
  }, [badgeAwarded]);

  if (!badgeAwarded) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/70 backdrop-blur-md z-[1050] flex items-center justify-center p-4 overflow-hidden"
        onClick={clearBadgeAwarded}
      >
        {showConfetti && <ConfettiExplosion />}
        
        <motion.div
          initial={{ scale: 0.5, rotate: -10, y: 50 }}
          animate={{ scale: 1, rotate: 0, y: 0 }}
          exit={{ scale: 0.5, rotate: 10, opacity: 0 }}
          transition={{ type: "spring", damping: 15, stiffness: 300 }}
          onClick={(e) => e.stopPropagation()}
          className="max-w-md w-full relative"
        >
          {/* Ambient Glow */}
          <div className="absolute inset-0 bg-brand-orange/30 blur-[100px] rounded-full -z-10 animate-pulse"></div>

          <div className="bg-white rounded-[40px] border border-white/20 shadow-2xl relative overflow-hidden">
            {/* Header pattern */}
            <div className="absolute top-0 left-0 w-full h-32 bg-gradient-to-br from-brand-orange to-orange-600 opacity-10"></div>
            
            <div className="p-8 text-center relative z-10">
              {/* Close button */}
              <button
                onClick={clearBadgeAwarded}
                className="absolute top-0 right-0 p-4 text-gray-400 hover:text-brand-orange transition-colors"
                title="Fechar"
              >
                <X size={20} />
              </button>

              {/* Trophy icon */}
              <motion.div
                className="flex justify-center mb-6"
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
              >
                <div className="w-24 h-24 bg-gradient-to-br from-yellow-400 via-brand-orange to-orange-600 rounded-full flex items-center justify-center shadow-xl relative">
                  <Trophy className="w-12 h-12 text-white drop-shadow-lg" />
                  <motion.div 
                    animate={{ rotate: 360 }}
                    transition={{ duration: 10, repeat: Infinity, ease: "linear" }}
                    className="absolute -inset-1 border-2 border-dashed border-white/30 rounded-full"
                  />
                  {/* Decorative Stars */}
                   <Star className="absolute -top-1 -right-1 text-yellow-300 fill-yellow-300 w-6 h-6 animate-bounce" />
                </div>
              </motion.div>

              {/* Badge info */}
              <motion.div
                className="mb-6 px-4"
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.4 }}
              >
                <div className="text-5xl mb-4">{badgeAwarded.icon || '🏆'}</div>
                <h2 className="text-3xl font-black text-gray-900 mb-2 tracking-tight">
                  {badgeAwarded.name || 'Nova Conquista!'}
                </h2>
                <p className="text-gray-500 font-medium leading-relaxed">
                  {badgeAwarded.description || 'Você desbloqueou algo incrível na Nossa Pesca!'}
                </p>
              </motion.div>

              {/* Points badge */}
              <motion.div
                className="inline-flex items-center gap-2 bg-brand-blue-deep/5 text-brand-blue-deep px-6 py-2 rounded-full mb-8 border border-brand-blue-deep/10"
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.6, type: "spring" }}
              >
                <Sparkles size={18} className="animate-pulse" />
                <span className="font-bold text-lg">+{badgeAwarded.points || 50} Pontos de Experiência</span>
              </motion.div>

              {/* Congratulations */}
              <motion.div
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.8 }}
                className="space-y-6"
              >
                <div className="h-px bg-gray-100 w-full"></div>
                
                <p className="text-gray-600 text-sm leading-snug">
                  🎉 **Parabéns!** Você está se tornando um mestre da Nossa Pesca. 
                  Continue explorando para fisgar mais conquistas!
                </p>

                <button
                  onClick={clearBadgeAwarded}
                  className="w-full bg-brand-orange hover:bg-orange-600 text-white font-bold py-4 rounded-2xl shadow-lg shadow-brand-orange/20 transition-all duration-300 transform hover:scale-[1.02] active:scale-95 flex items-center justify-center gap-2"
                >
                  Continuar Explorando
                </button>
              </motion.div>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
